'use client'

import React from 'react'

import { containerClassName } from '@/components/Container'
import { LocationHeroUniversityTravelStats } from '@/components/Location/LocationHeroUniversityTravelStats'
import { LocationMapHeroMap } from '@/components/Location/LocationMapHeroMap'
import { LocationMapUniversityFinderControls } from '@/components/Location/LocationMapUniversityFinderControls'
import { LocationUniversitySearch } from '@/components/Location/LocationUniversitySearch'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import { useCityMapUniversityFinder } from '@/lib/locations/useCityMapUniversityFinder'
import type { City } from '@/lib/mock/cities'
import type { CityId } from '@/lib/mock/mapLocations'
import { isCityId } from '@/lib/mock/mapLocations'
import type { LocationPage } from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import { useMessages } from '@/lib/i18n/useMessages'

type LocationMapHeroSectionProps =
  | {
      city: City
      location?: never
      locationCards: MapLocationCardData[]
    }
  | {
      city?: never
      location: LocationPage
      locationCards: MapLocationCardData[]
    }

function resolveCitySlug(props: LocationMapHeroSectionProps): CityId | undefined {
  if (props.city) {
    return isCityId(props.city.slug) ? props.city.slug : undefined
  }

  return props.location.citySlug
}

export function LocationMapHeroSection(props: LocationMapHeroSectionProps) {
  const messages = useMessages()
  const mapCopy = messages.mapControls
  const mapHeroCopy = messages.location.mapHero
  const citySlug = resolveCitySlug(props)
  const isCityMode = Boolean(props.city)
  const city = props.city
  const fixedLocation = props.location
  const mapContainerRef = React.useRef<HTMLDivElement>(null)

  const finder = useCityMapUniversityFinder({
    city,
    citySlug,
    fixedLocation,
    isCityMode,
    mapContainerRef,
    mapHeroCopy: {
      approximateDistanceLabel: mapHeroCopy.approximateDistanceLabel,
      distanceFromToLabel: mapHeroCopy.distanceFromToLabel,
      distanceToLabel: mapHeroCopy.distanceToLabel,
      proximityLabel: mapHeroCopy.proximityLabel,
      routeUnavailable: mapHeroCopy.routeUnavailable,
    },
  })

  const headingSegments = finder.isUniversityFinder
    ? [
        {
          finalColor: 'var(--brand-bluetext)',
          text: mapHeroCopy.finderTitleBeforeHighlight,
          trail: false,
        },
        {
          finalColor: 'var(--brand-dark-green)',
          text: mapHeroCopy.finderTitleHighlight,
        },
      ]
    : !isCityMode
      ? [
          {
            finalColor: 'var(--brand-bluetext)',
            text: mapHeroCopy.titleBeforeHighlight,
            trail: false,
          },
          {
            finalColor: 'var(--brand-dark-green)',
            text: mapHeroCopy.titleHighlight,
          },
          {
            finalColor: 'var(--brand-bluetext)',
            text: mapHeroCopy.titleAfterHighlight,
            trail: false,
          },
        ]
      : [
          {
            finalColor: 'var(--brand-bluetext)',
            text:
              city?.areaMap.titleBeforeHighlight ??
              finder.activeLocation?.areaMap.titleBeforeHighlight ??
              'All your student life essentials ',
            trail: false,
          },
          {
            finalColor: 'var(--brand-dark-green)',
            text:
              city?.areaMap.titleHighlight ??
              finder.activeLocation?.areaMap.titleHighlight ??
              'right around the corner',
          },
        ]
  const headingDescription = finder.isUniversityFinder
    ? mapHeroCopy.helperText
    : !isCityMode
      ? mapHeroCopy.description
      : null
  const exploreLabel =
    city?.areaMap.ctaLabel ?? finder.activeLocation?.areaMap.ctaLabel ?? 'Explore the area'
  const mapAlt = city?.areaMap.mapAlt ?? finder.activeLocation?.areaMap.mapAlt ?? ''

  if (!citySlug) {
    return null
  }

  if (!finder.heroMapViewport || finder.heroMapViewport.markers.length === 0) {
    return null
  }

  if (!isCityMode && !finder.activeLocation) {
    return null
  }

  return (
    <section
      aria-labelledby="location-map-hero-heading"
      className={cn(
        'location-map-hero-section bg-cream max-md:flex max-md:flex-col-reverse max-md:pb-6 md:relative',
        finder.isUniversityFinder ? 'md:min-h-[40rem]' : 'md:min-h-[48rem]',
      )}
      data-location-map-hero-section
    >
      <div
        className={cn(
          'locations-map-section z-0 overflow-hidden max-md:relative max-md:isolate max-md:mt-4 max-md:w-full max-md:shrink-0 md:absolute md:inset-0',
          finder.showUniversityRoute ? 'max-md:h-[24rem]' : 'max-md:h-[20rem]',
        )}
        data-location-map-hero-map
        ref={mapContainerRef}
      >
        <LocationMapHeroMap
          center={finder.heroMapViewport.center}
          fallbackImage={finder.fallbackImage}
          fallbackImageAlt={finder.fallbackImageAlt}
          locationCards={props.locationCards}
          mapCameraPadding={finder.mapCameraPadding}
          mapControlLabels={{
            zoomGroupLabel: mapCopy.zoomGroupLabel,
            zoomInLabel: mapCopy.zoomInLabel,
            zoomOutLabel: mapCopy.zoomOutLabel,
          }}
          markers={finder.heroMapViewport.markers}
          routeMarker={finder.routeMarker}
          routeAnimationId={finder.routeAnimationId}
          routeDirection={finder.routeDirection}
          selectedCampusSlug={finder.selectedCampusSlug}
          universityCoordinate={finder.universityCoordinate}
          universityName={finder.selectedUniversity?.name}
          zoom={finder.heroMapViewport.zoom}
        />
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 z-[2]',
            'bg-[linear-gradient(to_bottom,#FBFAF1_0%,rgba(251,250,241,0)_12%),linear-gradient(to_top,#FBFAF1_0%,rgba(251,250,241,0)_12%)]',
            'md:bg-[linear-gradient(to_bottom,#FBFAF1_0%,rgba(251,250,241,0)_10%),linear-gradient(to_top,#FBFAF1_0%,rgba(251,250,241,0)_10%),linear-gradient(to_right,#FBFAF1_0%,rgba(251,250,241,0.92)_45%,rgba(251,250,241,0)_60%)]',
          )}
        />
      </div>

      <div
        className={cn(
          containerClassName,
          'pointer-events-none relative z-30 !px-4 !py-6 md:!px-20 lg:!px-6',
          finder.isUniversityFinder ? 'md:!pt-16 md:!pb-8' : 'md:!py-16',
        )}
      >
        <div
          className="pointer-events-none max-w-[40rem] md:pt-24"
          data-locations-map-content
        >
          <ColorTrailText
            as="h2"
            className="pointer-events-auto font-display text-[2.5rem] font-medium leading-10 tracking-[-0.04em] text-bluetext md:text-[4rem] md:font-semibold md:leading-[1]"
            id="location-map-hero-heading"
            segments={headingSegments}
          />

          {headingDescription ? (
            <p className="pointer-events-auto mt-4 max-w-[36rem] font-outfit text-base font-medium text-dark-800/80">
              {headingDescription}
            </p>
          ) : null}

          {isCityMode ? (
            <LocationMapUniversityFinderControls
              campuses={finder.campuses}
              citySlug={citySlug}
              exploreLabel={exploreLabel}
              isUniversityFinder={finder.isUniversityFinder}
              mapHeroCopy={mapHeroCopy}
              onCampusClear={finder.handleCampusClear}
              onCampusSelect={finder.handleCampusSelect}
              onUniversityClear={finder.handleUniversityClear}
              onUniversitySelect={finder.handleUniversitySelect}
              routeUnavailableMessage={finder.routeUnavailableMessage}
              selectedCampusSlug={finder.selectedCampusSlug}
              selectedUniversity={finder.selectedUniversity}
              universities={finder.universities}
              universityCampusOptions={finder.universityCampusOptions}
            />
          ) : (
            <div className="pointer-events-auto mt-6 flex w-full min-w-0 flex-col gap-2 md:mt-10">
              <LocationUniversitySearch
                accentSelected
                clearLabel={mapHeroCopy.clearUniversityLabel}
                emptyLabel={mapHeroCopy.emptyState}
                label={mapHeroCopy.selectCampusLabel}
                listAriaLabel={mapHeroCopy.universitiesListAriaLabel}
                onClear={finder.handleUniversityClear}
                onSelect={finder.handleUniversitySelect}
                placeholder={mapHeroCopy.selectUniversityPlaceholder}
                selectedLabelMaxLength={22}
                universities={finder.universityCampusOptions}
                uppercase
                value={finder.selectedUniversity}
                wrapMenuOptions
              />
            </div>
          )}

          {finder.universityTravelStats?.length && finder.selectedUniversity ? (
            <div
              className={cn(
                'pointer-events-auto',
                finder.isUniversityFinder ? 'mt-4 md:mt-6' : 'mt-6 md:mt-10',
                finder.isUniversityFinder && 'md:pb-6',
              )}
            >
              <LocationHeroUniversityTravelStats
                compact={finder.isUniversityFinder}
                destinationLabel={finder.travelStatsDestinationLabel}
                stats={finder.universityTravelStats}
                theme="light"
                universityName={finder.selectedUniversity.name}
              />
            </div>
          ) : null}
        </div>

        <p className="sr-only">{mapAlt}</p>
      </div>
    </section>
  )
}
